import React, {useCallback} from 'react';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import {isDoorFrontsMode} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {
    DEFAULT_ELEVATION_PRESET,
    DEFAULT_PERSPECTIVE_PRESET,
    PLAN_PRESET,
    isViewOptionId,
} from 'components/customer/RoomPlanner/helpers/plannerView';
import {
    getActiveViewPreset,
    getDoorFrontsAnimating,
    getDoorFrontsMode,
    getDoorFrontsPose,
    setDoorFrontsAnimating,
    getIsEditHeightView,
    getIsEditingRoomDimension,
    getIsTopView,
    getIsWireframeMode,
    getPlannerViewMode,
    getShowArrows,
    setDoorFrontsMode,
    setIsWireframeMode,
    setPreviewPosition,
    setShowArrows,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';

/**
 * 3D / Plan / Elevation, Fronts, Dimensions, and Print actions for the top bar.
 * @return {object} View state and click handlers
 */
export const useTopBarControls = () => {
    const dispatch = useAppDispatch();
    const {jobId, roomId} = useParams();
    const mode = useAppSelector(getPlannerViewMode);
    const activePreset = useAppSelector(getActiveViewPreset);
    const doorFrontsMode = useAppSelector(getDoorFrontsMode);
    const doorFrontsPose = useAppSelector(getDoorFrontsPose);
    const doorFrontsAnimating = useAppSelector(getDoorFrontsAnimating);
    const showArrows = useAppSelector(getShowArrows);
    const isWireframeMode = useAppSelector(getIsWireframeMode);
    const isTopView = useAppSelector(getIsTopView);
    const isEditHeightView = useAppSelector(getIsEditHeightView);
    const isEditingRoomDimension = useAppSelector(getIsEditingRoomDimension);
    const viewsLocked = isTopView || isEditHeightView || isEditingRoomDimension;

    const selectPreset = useCallback(
        (position: PreviewPosition) => {
            if (viewsLocked) return;
            dispatch(setPreviewPosition(position));
        },
        [dispatch, viewsLocked]
    );

    const select3D = useCallback(() => {
        if (viewsLocked || mode === '3D') return;
        dispatch(setPreviewPosition(DEFAULT_PERSPECTIVE_PRESET));
    }, [dispatch, mode, viewsLocked]);

    const selectPlan = useCallback(() => {
        if (viewsLocked) return;
        dispatch(setPreviewPosition(PLAN_PRESET));
    }, [dispatch, viewsLocked]);

    const selectElevation = useCallback(() => {
        if (viewsLocked || mode === 'ELEVATION') return;
        dispatch(setPreviewPosition(DEFAULT_ELEVATION_PRESET));
    }, [dispatch, mode, viewsLocked]);

    const resetView = useCallback(() => {
        if (viewsLocked || mode !== '3D') return;
        dispatch(setPreviewPosition(DEFAULT_PERSPECTIVE_PRESET));
    }, [dispatch, mode, viewsLocked]);

    const selectOption = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const preset = event.currentTarget.dataset.preset;
            if (preset && isViewOptionId(preset)) {
                selectPreset(preset);
            }
        },
        [selectPreset]
    );

    const toggleWireframe = useCallback(() => {
        dispatch(setIsWireframeMode(!isWireframeMode));
    }, [dispatch, isWireframeMode]);

    const selectDoorFront = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (doorFrontsAnimating) return;
            const next = event.currentTarget.dataset.mode;
            if (!next || !isDoorFrontsMode(next) || next === doorFrontsMode) {
                return;
            }
            if (next === 'open' || next === 'shut') {
                dispatch(setDoorFrontsAnimating(true));
            }
            dispatch(setDoorFrontsMode(next));
        },
        [dispatch, doorFrontsAnimating, doorFrontsMode]
    );

    const toggleDimensions = useCallback(() => {
        dispatch(setShowArrows(!showArrows));
    }, [dispatch, showArrows]);

    const printReport = useCallback(() => {
        if (jobId && roomId) {
            window.open(
                `/v2/pdf/3d-room-planner/job/${parseInt(jobId)}/room/${roomId}/report?pdf=true`
            );
        }
    }, [jobId, roomId]);

    return {
        mode,
        activePreset,
        doorFrontsMode,
        doorFrontsPose,
        doorFrontsAnimating,
        showArrows,
        isWireframeMode,
        viewsLocked,
        select3D,
        selectPlan,
        selectElevation,
        resetView,
        selectOption,
        toggleWireframe,
        selectDoorFront,
        toggleDimensions,
        printReport,
    };
};
